<!-- ==========================================================================
  ScheduleBadge.vue
  Summary banner for DeviceList.vue — sits above the device table.
  Add to DeviceList.vue:

    <schedule-badge @click-schedules="$router.push({ name: 'ScheduleList' })" />

  And in mounted() / the existing 30s interval, also dispatch:
    this.$store.dispatch('schedule/loadSummary')
=========================================================================== -->

<template>
  <transition name="fade">
    <div
      v-if="visible"
      class="schedule-badge mb-3"
      :class="urgencyClass"
    >
      <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap:8px;">
        <!-- Left: icon + message -->
        <div class="d-flex align-items-center">
          <i :class="iconClass" class="mr-3 fa-lg"></i>
          <div>
            <div class="font-weight-bold" style="font-size:0.9rem;">
              {{ headline }}
            </div>
            <div style="font-size:0.8rem; opacity:0.85;">
              {{ subline }}
            </div>
          </div>
        </div>

        <!-- Right: stat pills + link -->
        <div class="d-flex align-items-center" style="gap:8px;">
          <span v-if="summary.ready > 0" class="stat-pill pill-warning">
            <i class="fas fa-bolt mr-1"></i>{{ summary.ready }} ready
          </span>
          <span v-if="summary.in_progress > 0" class="stat-pill pill-info">
            <i class="fas fa-circle-notch fa-spin mr-1"></i>{{ summary.in_progress }} updating
          </span>
          <span v-if="needsAttention > 0" class="stat-pill pill-danger">
            <i class="fas fa-exclamation-triangle mr-1"></i>{{ needsAttention }} need attention
          </span>
          <base-button size="sm" type="white" @click="$emit('click-schedules')">
            View Schedules <i class="fas fa-arrow-right ml-1"></i>
          </base-button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'ScheduleBadge',

  computed: {
    summary() {
      return this.$store.getters.scheduleSummary || {}
    },
    needsAttention() {
      return this.$store.getters.needsAttention || 0
    },
    visible() {
      return (this.summary.active_schedules || 0) > 0
    },
    urgencyClass() {
      if (this.needsAttention > 0) return 'badge-danger-bg'
      if ((this.summary.ready || 0) > 0) return 'badge-warning-bg'
      return 'badge-info-bg'
    },
    iconClass() {
      if (this.needsAttention > 0) return 'fas fa-exclamation-triangle'
      if ((this.summary.ready || 0) > 0) return 'fas fa-bolt'
      return 'fas fa-sync-alt fa-spin'
    },
    headline() {
      const n = this.summary.active_schedules || 0
      return `${n} active update schedule${n !== 1 ? 's' : ''}`
    },
    subline() {
      const parts = []
      if (this.summary.pending > 0)     parts.push(`${this.summary.pending} waiting for charger`)
      if (this.summary.in_progress > 0) parts.push(`${this.summary.in_progress} updating now`)
      if (this.summary.completed > 0)   parts.push(`${this.summary.completed} done`)
      if (this.needsAttention > 0)      parts.push(`${this.needsAttention} need your attention`)
      return parts.join(' · ') || 'All devices pending'
    },
  },
}
</script>

<style scoped>
.schedule-badge {
  border-radius: 8px;
  padding: 12px 16px;
  border-left: 4px solid;
}
.badge-info-bg    { background: #e8f4fd; border-left-color: #11cdef; color: #0c5460; }
.badge-warning-bg { background: #fff8e6; border-left-color: #fb6340; color: #7d4400; }
.badge-danger-bg  { background: #fde8e8; border-left-color: #f5365c; color: #7d0d1e; }

.stat-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
}
.pill-warning { background: #fff3cd; color: #856404; }
.pill-info    { background: #d1ecf1; color: #0c5460; }
.pill-danger  { background: #f8d7da; color: #721c24; }

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to           { opacity: 0; }
</style>


<!-- ==========================================================================
  DeviceList.vue — what to add
  (These are snippets, not a full file replacement)
=========================================================================== -->

<!--
1. IMPORT + REGISTER ScheduleBadge at the top of DeviceList.vue:

  import ScheduleBadge from '@/views/components/ScheduleBadge'
  components: { ..., ScheduleBadge },


2. ADD the badge just above the el-table in the template:

  <schedule-badge @click-schedules="$router.push({ name: 'ScheduleList' })" />


3. ADD a schedule status column to the el-table column definitions.
   Put it after the "status" column:

  <el-table-column label="Schedule" width="110">
    <template #default="{ row }">
      <span v-if="row._scheduleEntry" :title="row._scheduleEntry.scheduleName">
        <i
          :class="scheduleEntryIcon(row._scheduleEntry.status)"
          :style="{ color: scheduleEntryColor(row._scheduleEntry.status) }"
        ></i>
        <small class="ml-1">{{ row._scheduleEntry.status }}</small>
      </span>
    </template>
  </el-table-column>


4. ADD to loadDevice() — after the existing device load dispatches, also reload summary:

  this.$store.dispatch('schedule/loadSummary')


5. ADD computed property to enrich device rows with their schedule entry:

  devicesWithSchedule() {
    const summary = this.$store.getters.scheduleSummary
    // Only enrich if there are active schedules (avoids unnecessary work)
    if (!summary || !summary.active_schedules) return this.filteredDevices

    const activeSchedule = this.$store.getters.schedules?.[0]  // simplified
    if (!activeSchedule) return this.filteredDevices

    return this.filteredDevices.map(device => {
      const entry = (activeSchedule.device_entries || [])
        .find(e => e.device_id === device.name)
      return entry
        ? { ...device, _scheduleEntry: { ...entry, scheduleName: activeSchedule.name } }
        : device
    })
  }

  // Then bind the table to devicesWithSchedule instead of filteredDevices.


6. ADD helper methods:

  scheduleEntryIcon(status) {
    const map = {
      pending:     'far fa-clock',
      ready:       'fas fa-bolt',
      in_progress: 'fas fa-circle-notch fa-spin',
      completed:   'fas fa-check-circle',
      failed:      'fas fa-times-circle',
      expired:     'fas fa-hourglass-end',
    }
    return map[status] || 'fas fa-circle'
  },
  scheduleEntryColor(status) {
    const map = {
      pending:     '#adb5bd',
      ready:       '#fb6340',
      in_progress: '#11cdef',
      completed:   '#2dce89',
      failed:      '#f5365c',
      expired:     '#f5365c',
    }
    return map[status] || '#adb5bd'
  },
-->
